import { actionQueueRank, matchesBuiltInViewWithContext } from "./screener-state";
import type {
  CanonicalScreenerSummary,
  CanonicalTransaction,
  MissingFieldFlag,
  ScreenerSortField,
  ScreenerState
} from "./types";

export type CanonicalScreenerCriteria = Pick<
  ScreenerState,
  "builtInViewId" | "filters" | "sorting"
>;

type CanonicalSortValue = string | number | null;
type CanonicalSortAccessor = (row: CanonicalTransaction) => CanonicalSortValue;

const CONFIDENCE_SORT_RANK = {
  low: 1,
  medium: 2,
  high: 3
} as const;

const CANONICAL_SORT_ACCESSORS: Record<ScreenerSortField, CanonicalSortAccessor> = {
  filingDate: (row) => row.filingDate,
  tradeDate: (row) => row.tradeDate,
  ticker: (row) => row.ticker,
  companyName: (row) => row.companyName,
  insiderName: (row) => row.insiderName,
  value: (row) => row.value,
  shares: (row) => row.shares,
  price: (row) => row.price,
  ownershipChangePct: (row) => row.ownershipChangePct,
  materialityBps: (row) => row.marketContext.valueToMarketCapBps,
  filingLagDays: (row) => row.filingLagDays,
  score: (row) => row.signal.score,
  confidence: (row) => CONFIDENCE_SORT_RANK[row.signal.confidence],
  distinctInsiderCount: (row) => row.cluster?.distinctInsiderCount ?? null
};

const CANONICAL_SEARCH_TEXT_CACHE = new WeakMap<CanonicalTransaction, string>();

function canonicalTextForSearch(row: CanonicalTransaction): string {
  const cached = CANONICAL_SEARCH_TEXT_CACHE.get(row);
  if (cached !== undefined) return cached;
  const normalized = `${row.ticker} ${row.companyName} ${row.insiderName} ${row.relationship} ${row.relationshipFlags.join(" ")} ${row.accessionNumber} ${row.industryDescription} ${row.securityTitle} ${row.securityStatus} ${row.flags.join(" ")} ${row.footnotes.join(" ")}`.toLowerCase();
  CANONICAL_SEARCH_TEXT_CACHE.set(row, normalized);
  return normalized;
}

function isMissingCanonicalField(row: CanonicalTransaction, field: MissingFieldFlag): boolean {
  switch (field) {
    case "tradeDate":
      return row.tradeDate === null;
    case "price":
      return row.price === null;
    case "value":
      return row.value === null;
    case "holdingsBefore":
      return row.holdingsBefore === null;
    case "holdingsAfter":
      return row.holdingsAfter === null;
    case "ownerCik":
      return row.ownerCik === null || row.ownerCik.trim() === "";
    case "ownerIdentityKey":
      return row.ownerIdentityKey === null || row.ownerIdentityKey.trim() === "";
    case "filingLagDays":
      return row.filingLagDays === null;
  }
}

function outsideNullableRange(
  value: number | null,
  minimum: number | null,
  maximum: number | null
): boolean {
  if (minimum === null && maximum === null) return false;
  if (value === null) return true;
  return (minimum !== null && value < minimum) || (maximum !== null && value > maximum);
}

function compareCanonicalValues(
  left: CanonicalSortValue,
  right: CanonicalSortValue,
  descending: boolean
): number {
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  let result: number;
  if (typeof left === "number" && typeof right === "number") {
    result = left - right;
  } else {
    result = left < right ? -1 : left > right ? 1 : 0;
  }
  return descending ? -result : result;
}

export function applyCanonicalScreener(
  rows: readonly CanonicalTransaction[],
  state: CanonicalScreenerCriteria,
  favoriteTickers: readonly string[] = []
): CanonicalTransaction[] {
  const filters = state.filters;
  const favorites = new Set(favoriteTickers.map((ticker) => ticker.toUpperCase()));
  let latestFilingDate: string | null = null;
  for (const row of rows) {
    if (latestFilingDate === null || row.filingDate > latestFilingDate) {
      latestFilingDate = row.filingDate;
    }
  }
  const viewContext = { latestFilingDate };
  const signalBuckets = new Set(filters.signalBuckets);
  const relationshipFlags = new Set(filters.relationshipFlags);
  const planStatuses = new Set(filters.planStatuses);
  const exchanges = new Set(filters.exchanges);
  const transactionActivities = new Set(filters.transactionActivities);
  const transactionCodes = new Set(filters.transactionCodes);
  const securityStatuses = new Set(filters.securityStatuses);
  const confidences = new Set(filters.confidences);
  const amendmentStates = new Set(filters.amendmentStates);
  const valueQualities = new Set(filters.valueQualities);
  const missingFieldFlags = new Set(filters.missingFieldFlags);
  const marketCapBuckets = new Set(filters.marketCapBuckets ?? []);
  const query = filters.query.trim().toLowerCase();
  const industryDescription = filters.industryDescription.trim().toLowerCase();
  const accessionNumber = filters.accessionNumber.trim().toLowerCase();
  const hasFilingDateRange = filters.filingDateFrom !== null || filters.filingDateTo !== null;
  const hasTradeDateRange = filters.tradeDateFrom !== null || filters.tradeDateTo !== null;

  const filtered = rows.filter((row) => {
    if (!matchesBuiltInViewWithContext(state.builtInViewId, row, favorites, viewContext)) return false;
    if (filters.favoritesOnly && !favorites.has(row.ticker.toUpperCase())) return false;
    if (query !== "" && !canonicalTextForSearch(row).includes(query)) return false;

    if (
      hasFilingDateRange &&
      ((filters.filingDateFrom !== null && row.filingDate < filters.filingDateFrom) ||
        (filters.filingDateTo !== null && row.filingDate > filters.filingDateTo))
    ) {
      return false;
    }
    if (hasTradeDateRange) {
      if (row.tradeDate === null) return false;
      if (filters.tradeDateFrom !== null && row.tradeDate < filters.tradeDateFrom) return false;
      if (filters.tradeDateTo !== null && row.tradeDate > filters.tradeDateTo) return false;
    }
    if (signalBuckets.size > 0 && !signalBuckets.has(row.signal.bucket)) return false;
    if (
      relationshipFlags.size > 0 &&
      !row.relationshipFlags.some((relationshipFlag) => relationshipFlags.has(relationshipFlag))
    ) {
      return false;
    }
    if (
      outsideNullableRange(
        row.value,
        filters.minTransactionValue,
        filters.maxTransactionValue
      )
    ) {
      return false;
    }
    if (planStatuses.size > 0 && !planStatuses.has(row.plan.status)) return false;
    if (
      filters.minDistinctInsiders !== null &&
      (row.cluster === null || row.cluster.distinctInsiderCount < filters.minDistinctInsiders)
    ) {
      return false;
    }
    if (exchanges.size > 0 && !exchanges.has(row.exchange)) return false;
    if (transactionActivities.size > 0 && !transactionActivities.has(row.tradeType)) return false;
    if (transactionCodes.size > 0 && !transactionCodes.has(row.transactionCode)) return false;
    if (securityStatuses.size > 0 && !securityStatuses.has(row.securityStatus)) return false;
    if (outsideNullableRange(row.shares, filters.minShares, filters.maxShares)) return false;
    if (outsideNullableRange(row.price, filters.minPrice, filters.maxPrice)) return false;
    if (
      outsideNullableRange(
        row.ownershipChangePct,
        filters.minHoldingsChange,
        filters.maxHoldingsChange
      )
    ) {
      return false;
    }
    if (confidences.size > 0 && !confidences.has(row.signal.confidence)) return false;
    if (amendmentStates.size > 0 && !amendmentStates.has(row.amendmentState)) return false;
    if (valueQualities.size > 0 && !valueQualities.has(row.valueQuality)) return false;
    if (missingFieldFlags.size > 0) {
      let matchesMissingField = false;
      for (const field of missingFieldFlags) {
        if (isMissingCanonicalField(row, field)) {
          matchesMissingField = true;
          break;
        }
      }
      if (!matchesMissingField) return false;
    }
    if (
      industryDescription !== "" &&
      !row.industryDescription.toLowerCase().includes(industryDescription)
    ) {
      return false;
    }
    if (
      outsideNullableRange(
        row.filingLagDays,
        filters.minFilingLagDays,
        filters.maxFilingLagDays
      )
    ) {
      return false;
    }
    if (
      accessionNumber !== "" &&
      !row.accessionNumber.toLowerCase().includes(accessionNumber)
    ) {
      return false;
    }
    const marketContext = row.marketContext;
    if (marketCapBuckets.size > 0) {
      if (!marketContext || !marketCapBuckets.has(marketContext.marketCapBucket)) return false;
    }
    if (
      outsideNullableRange(
        marketContext?.valueToMarketCapBps ?? null,
        filters.minValueToMarketCapBps ?? null,
        null
      )
    ) {
      return false;
    }
    return true;
  });

  const sorting = state.sorting.map((sort) => ({
    accessor: CANONICAL_SORT_ACCESSORS[sort.id],
    descending: sort.desc
  }));
  return filtered.sort((left, right) => {
    if (state.builtInViewId === "action-queue") {
      const rankDelta = actionQueueRank(right) - actionQueueRank(left);
      if (rankDelta !== 0) return rankDelta;
    }
    for (const sort of sorting) {
      const comparison = compareCanonicalValues(
        sort.accessor(left),
        sort.accessor(right),
        sort.descending
      );
      if (comparison !== 0) return comparison;
    }
    return left.transactionId < right.transactionId
      ? -1
      : left.transactionId > right.transactionId
        ? 1
        : 0;
  });
}

export function buildCanonicalSummary(
  rows: readonly CanonicalTransaction[]
): CanonicalScreenerSummary {
  let informativeBuyCount = 0;
  let informativeSellCount = 0;
  let informativeBuyNotional = 0;
  let informativeSellNotional = 0;
  let pricedRowCount = 0;
  const scores: number[] = [];

  for (const row of rows) {
    const isUsableNotional =
      row.value !== null &&
      (row.valueQuality === "reported" || row.valueQuality === "derived");
    if (row.signal.bucket === "informative_buy") {
      informativeBuyCount += 1;
      if (isUsableNotional) informativeBuyNotional += row.value!;
    } else if (row.signal.bucket === "informative_sell") {
      informativeSellCount += 1;
      if (isUsableNotional) informativeSellNotional += row.value!;
    }
    if (isUsableNotional) pricedRowCount += 1;
    if (row.signal.score !== null) scores.push(row.signal.score);
  }

  scores.sort((left, right) => left - right);
  const middle = Math.floor(scores.length / 2);
  const medianScore =
    scores.length === 0
      ? null
      : scores.length % 2 === 0
        ? (scores[middle - 1]! + scores[middle]!) / 2
        : scores[middle]!;

  return {
    totalRows: rows.length,
    informativeBuyCount,
    informativeSellCount,
    informativeBuyNotional,
    informativeSellNotional,
    pricedRowCount,
    pricedCoveragePercent: rows.length === 0 ? 0 : (pricedRowCount / rows.length) * 100,
    medianScore
  };
}
